import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { customerAPI, paymentAPI, complaintAPI, catvAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ArrowLeft, Phone, MapPin, Calendar, CreditCard, MessageSquare, Edit2, ExternalLink, Printer, Save, X } from 'lucide-react';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtDate = d => d ? format(new Date(d), 'dd MMM yyyy') : '—';
const daysLeft = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

function ExpiryBadge({ date }) {
  const d = daysLeft(date);
  if (d === null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (d < 0) return <span className="badge badge-danger">Expired {Math.abs(d)}d ago</span>;
  if (d === 0) return <span className="badge badge-danger">Expires Today</span>;
  if (d <= 3) return <span className="badge badge-warning">{d} days left</span>;
  if (d <= 7) return <span className="badge badge-info">{d} days left</span>;
  return <span style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>{fmtDate(date)}</span>;
}

function PaymentModal({ customer, onClose, onSuccess }) {
  const planPrice = customer.catv?.package?.price || customer.broadband?.plan?.price || '';
  const [form, setForm] = useState({
    paymentFor: customer.customerType === 'catv' ? 'catv_renewal' : 'broadband_renewal',
    amount: String(planPrice),
    paymentMethod: 'cash',
    transactionId: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handlePay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await paymentAPI.create({ customer: customer._id, ...form, amount: parseFloat(form.amount) });
      toast.success(`Payment recorded! Receipt: ${data.data.receiptNumber}`);
      onSuccess(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Record Payment — {customer.name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handlePay}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Payment For</label>
              <select className="form-control" value={form.paymentFor} onChange={e => setForm(f => ({ ...f, paymentFor: e.target.value }))}>
                {(customer.customerType === 'broadband' || customer.customerType === 'both') && <option value="broadband_renewal">Broadband Renewal</option>}
                {(customer.customerType === 'catv' || customer.customerType === 'both') && <option value="catv_renewal">CATV Renewal</option>}
                <option value="installation">Installation</option>
                <option value="security_deposit">Security Deposit</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Amount (₹) *</label>
                <input type="number" className="form-control" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select className="form-control" value={form.paymentMethod} onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="online">Online</option>
                  <option value="staff_collection">Staff Collection</option>
                </select>
              </div>
            </div>
            {(form.paymentMethod === 'upi' || form.paymentMethod === 'bank_transfer' || form.paymentMethod === 'online') && (
              <div className="form-group">
                <label className="form-label">Transaction ID</label>
                <input className="form-control font-mono" value={form.transactionId} onChange={e => setForm(f => ({ ...f, transactionId: e.target.value }))} placeholder="Transaction reference" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              <CreditCard size={14} /> {loading ? 'Processing...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditCustomerModal({ customer, onClose, onSuccess }) {
  const toInputDate = (d) => d ? format(new Date(d), 'yyyy-MM-dd') : '';

  const existingPkgId = customer.catv?.package?._id || (typeof customer.catv?.package === 'string' ? customer.catv.package : '') || '';

  const [form, setForm] = useState({
    name: customer.name || '',
    mobile: customer.mobile || '',
    alternateMobile: customer.alternateMobile || '',
    email: customer.email || '',
    status: customer.status || 'active',
    notes: customer.notes || '',
    address: {
      line1: customer.address?.line1 || '',
      area: customer.address?.area || '',
      city: customer.address?.city || '',
      pincode: customer.address?.pincode || '',
      landmark: customer.address?.landmark || '',
    },
    catv: {
      packageId: existingPkgId,
      setTopBoxSerial: customer.catv?.setTopBoxSerial || '',
      vcNumber: customer.catv?.vcNumber || '',
      expiryDate: toInputDate(customer.catv?.expiryDate),
      isActive: customer.catv?.isActive ?? false,
    },
    broadband: {
      username: customer.broadband?.username || '',
      staticIp: customer.broadband?.staticIp || '',
      expiryDate: toInputDate(customer.broadband?.expiryDate),
      isActive: customer.broadband?.isActive ?? false,
    },
  });
  const [loading, setLoading] = useState(false);
  const [catvPackages, setCatvPackages] = useState([]);

  useEffect(() => {
    if (customer.customerType === 'catv' || customer.customerType === 'both') {
      catvAPI.getPackages({ limit: 100 }).then(r => setCatvPackages(r.data?.data || [])).catch(() => {});
    }
  }, [customer.customerType]);

  const set = (path, val) => setForm(prev => {
    const parts = path.split('.');
    if (parts.length === 1) return { ...prev, [path]: val };
    return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: val } };
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        mobile: form.mobile,
        alternateMobile: form.alternateMobile,
        email: form.email,
        status: form.status,
        notes: form.notes,
        address: form.address,
      };
      if (customer.customerType === 'catv' || customer.customerType === 'both') {
        payload.catv = {
          ...customer.catv,
          setTopBoxSerial: form.catv.setTopBoxSerial,
          vcNumber: form.catv.vcNumber,
          expiryDate: form.catv.expiryDate || undefined,
          isActive: form.catv.isActive,
          package: form.catv.packageId || customer.catv?.package?._id || customer.catv?.package || undefined,
        };
      }
      if (customer.customerType === 'broadband' || customer.customerType === 'both') {
        payload.broadband = {
          ...customer.broadband,
          username: form.broadband.username,
          staticIp: form.broadband.staticIp,
          expiryDate: form.broadband.expiryDate || undefined,
          isActive: form.broadband.isActive,
          plan: customer.broadband?.plan?._id || customer.broadband?.plan,
        };
      }
      const { data } = await customerAPI.update(customer._id, payload);
      toast.success('Customer updated successfully');
      onSuccess(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: '.88rem', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '.78rem', color: 'var(--text-muted)', fontWeight: 500, marginBottom: 3, display: 'block' };
  const sectionStyle = { marginBottom: 20 };
  const sectionTitle = { fontSize: '.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--primary)', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--border)' };
  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600, width: '95vw' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Customer — {customer.name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>

            {/* Basic Info */}
            <div style={sectionStyle}>
              <p style={sectionTitle}>Basic Information</p>
              <div style={grid2}>
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} required />
                </div>
                <div>
                  <label style={labelStyle}>Mobile</label>
                  <input style={inputStyle} value={form.mobile} onChange={e => set('mobile', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Alternate Mobile</label>
                  <input style={inputStyle} value={form.alternateMobile} onChange={e => set('alternateMobile', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="disconnected">Disconnected</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Address */}
            <div style={sectionStyle}>
              <p style={sectionTitle}>Address</p>
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>Address Line 1 *</label>
                <input style={inputStyle} value={form.address.line1} onChange={e => set('address.line1', e.target.value)} required />
              </div>
              <div style={grid2}>
                <div>
                  <label style={labelStyle}>Area *</label>
                  <input style={inputStyle} value={form.address.area} onChange={e => set('address.area', e.target.value)} required />
                </div>
                <div>
                  <label style={labelStyle}>City</label>
                  <input style={inputStyle} value={form.address.city} onChange={e => set('address.city', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Pincode</label>
                  <input style={inputStyle} value={form.address.pincode} onChange={e => set('address.pincode', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Landmark</label>
                  <input style={inputStyle} value={form.address.landmark} onChange={e => set('address.landmark', e.target.value)} />
                </div>
              </div>
            </div>

            {/* CATV */}
            {(customer.customerType === 'catv' || customer.customerType === 'both') && (
              <div style={sectionStyle}>
                <p style={sectionTitle}>📺 CATV Subscription</p>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}>Package</label>
                  <select style={inputStyle} value={form.catv.packageId}
                    onChange={e => set('catv.packageId', e.target.value)}>
                    <option value="">— No package assigned —</option>
                    {catvPackages.map(p => (
                      <option key={p._id} value={p._id}>
                        {p.displayName || p.name} — ₹{p.price}/mo
                      </option>
                    ))}
                  </select>
                </div>
                <div style={grid2}>
                  <div>
                    <label style={labelStyle}>STB Serial</label>
                    <input style={inputStyle} value={form.catv.setTopBoxSerial} onChange={e => set('catv.setTopBoxSerial', e.target.value)} />
                  </div>
                  <div>
                    <label style={labelStyle}>VC Number</label>
                    <input style={inputStyle} value={form.catv.vcNumber} onChange={e => set('catv.vcNumber', e.target.value)} />
                  </div>
                  <div>
                    <label style={labelStyle}>Expiry Date</label>
                    <input style={inputStyle} type="date" value={form.catv.expiryDate} onChange={e => set('catv.expiryDate', e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                    <input type="checkbox" id="catvActive" checked={form.catv.isActive} onChange={e => set('catv.isActive', e.target.checked)} />
                    <label htmlFor="catvActive" style={{ fontSize: '.88rem', cursor: 'pointer' }}>CATV Active</label>
                  </div>
                </div>
              </div>
            )}

            {/* Broadband */}
            {(customer.customerType === 'broadband' || customer.customerType === 'both') && (
              <div style={sectionStyle}>
                <p style={sectionTitle}>🌐 Broadband Subscription</p>
                <div style={grid2}>
                  <div>
                    <label style={labelStyle}>Username (PPPoE)</label>
                    <input style={inputStyle} value={form.broadband.username} onChange={e => set('broadband.username', e.target.value)} />
                  </div>
                  <div>
                    <label style={labelStyle}>Static IP</label>
                    <input style={inputStyle} value={form.broadband.staticIp} onChange={e => set('broadband.staticIp', e.target.value)} />
                  </div>
                  <div>
                    <label style={labelStyle}>Expiry Date</label>
                    <input style={inputStyle} type="date" value={form.broadband.expiryDate} onChange={e => set('broadband.expiryDate', e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                    <input type="checkbox" id="bbActive" checked={form.broadband.isActive} onChange={e => set('broadband.isActive', e.target.checked)} />
                    <label htmlFor="bbActive" style={{ fontSize: '.88rem', cursor: 'pointer' }}>Broadband Active</label>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}><X size={14} /> Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}><Save size={14} /> {loading ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(searchParams.get('edit') === '1');
  const [activeTab, setActiveTab] = useState('info');

  const loadData = async () => {
    try {
      const [cd, pd, co] = await Promise.all([
        customerAPI.getById(id),
        customerAPI.getPayments(id),
        customerAPI.getComplaints(id),
      ]);
      setCustomer(cd.data.data);
      setPayments(pd.data.data);
      setComplaints(co.data.data);
    } catch {
      toast.error('Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [id]);

  if (loading) return <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>;
  if (!customer) return <div className="empty-state"><h3>Customer not found</h3></div>;

  const statusBadge = { active: 'success', suspended: 'warning', disconnected: 'danger', pending: 'info' };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
            <ArrowLeft size={14} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>{customer.name}</h2>
              <span className={`badge badge-${statusBadge[customer.status]}`} style={{ textTransform: 'capitalize' }}>{customer.status}</span>
            </div>
            <p className="text-muted text-small">
              <span className="font-mono">{customer.customerId}</span> · {customer.mobile}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
            <Edit2 size={14} /> Edit Customer
          </button>
          <button className="btn btn-primary" onClick={() => setShowPayModal(true)}>
            <CreditCard size={14} /> Collect Payment
          </button>
          <Link to={`/complaints`} className="btn btn-secondary">
            <MessageSquare size={14} /> New Ticket
          </Link>
        </div>
      </div>

      {/* Quick info strip */}
      <div className="card mb-4" style={{ padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem' }}>
            <Phone size={13} color="var(--text-muted)" />{customer.mobile}
            {customer.alternateMobile && <span style={{ color: 'var(--text-muted)' }}>/ {customer.alternateMobile}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem' }}>
            <MapPin size={13} color="var(--text-muted)" />{customer.address?.line1}, {customer.address?.area}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem' }}>
            <Calendar size={13} color="var(--text-muted)" />Installed: {fmtDate(customer.installationDate)}
          </div>
          {customer.outstandingBalance > 0 && (
            <div style={{ marginLeft: 'auto', background: '#fee2e2', padding: '4px 12px', borderRadius: 6 }}>
              <span style={{ color: 'var(--danger)', fontWeight: 700, fontSize: '.85rem' }}>
                Outstanding: ₹{fmt(customer.outstandingBalance)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '2px solid var(--border)' }}>
        {['info', 'payments', 'complaints'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '8px 16px', border: 'none', background: 'none',
            fontWeight: activeTab === tab ? 600 : 400,
            color: activeTab === tab ? 'var(--primary)' : 'var(--text-muted)',
            borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2, cursor: 'pointer', textTransform: 'capitalize', fontSize: '.9rem'
          }}>
            {tab} {tab === 'payments' && `(${payments.length})`} {tab === 'complaints' && `(${complaints.length})`}
          </button>
        ))}
      </div>

      {activeTab === 'info' && (
        <div className="grid-2">
          {(customer.customerType === 'broadband' || customer.customerType === 'both') && (
            <div className="card">
              <div className="card-header"><h3>🌐 Broadband</h3></div>
              <div className="card-body">
                <div style={{ display: 'grid', gap: 10 }}>
                  <Row label="Plan" value={customer.broadband?.plan?.displayName || customer.broadband?.plan?.name || '—'} />
                  <Row label="Speed" value={customer.broadband?.plan ? `${customer.broadband.plan.speed?.download}/${customer.broadband.plan.speed?.upload} Mbps` : '—'} />
                  <Row label="Price" value={customer.broadband?.plan ? `₹${customer.broadband.plan.price}/mo` : '—'} />
                  <Row label="Username" value={customer.broadband?.username} mono />
                  <Row label="Static IP" value={customer.broadband?.staticIp} mono />
                  <Row label="Expiry" value={<ExpiryBadge date={customer.broadband?.expiryDate} />} />
                </div>
              </div>
            </div>
          )}

          {(customer.customerType === 'catv' || customer.customerType === 'both') && (
            <div className="card">
              <div className="card-header"><h3>📺 CATV</h3></div>
              <div className="card-body">
                <div style={{ display: 'grid', gap: 10 }}>
                  <Row label="Package" value={customer.catv?.package?.displayName || customer.catv?.package?.name || '—'} />
                  <Row label="Price" value={customer.catv?.package ? `₹${customer.catv.package.price}/mo` : '—'} />
                  <Row label="STB Serial" value={customer.catv?.setTopBoxSerial} mono />
                  <Row label="VC Number" value={customer.catv?.vcNumber} mono />
                  <Row label="Expiry" value={<ExpiryBadge date={customer.catv?.expiryDate} />} />
                </div>
              </div>
            </div>
          )}

          {customer.notes && (
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header"><h3>Notes</h3></div>
              <div className="card-body"><p style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>{customer.notes}</p></div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="card">
          <div className="table-container">
            {payments.length === 0 ? (
              <div className="empty-state"><CreditCard size={28} /><h3>No payments yet</h3></div>
            ) : (
              <table>
                <thead>
                  <tr><th>Receipt</th><th>Date</th><th>For</th><th>Method</th><th>Amount</th><th>Staff</th><th></th></tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p._id}>
                      <td className="font-mono" style={{ fontSize: '.8rem' }}>{p.receiptNumber}</td>
                      <td>{fmtDate(p.paymentDate)}</td>
                      <td style={{ textTransform: 'capitalize', fontSize: '.82rem' }}>{p.paymentFor?.replace(/_/g, ' ')}</td>
                      <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{p.paymentMethod}</span></td>
                      <td style={{ fontWeight: 700, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</td>
                      <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{p.collectedBy?.name}</td>
                      <td>
                        <a href={`/api/v1/payments/${p._id}/receipt`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                          <Printer size={12} /> Receipt
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'complaints' && (
        <div className="card">
          <div className="table-container">
            {complaints.length === 0 ? (
              <div className="empty-state"><MessageSquare size={28} /><h3>No complaints</h3></div>
            ) : (
              <table>
                <thead>
                  <tr><th>Ticket</th><th>Category</th><th>Status</th><th>Priority</th><th>Raised</th><th></th></tr>
                </thead>
                <tbody>
                  {complaints.map(c => (
                    <tr key={c._id}>
                      <td className="font-mono" style={{ fontSize: '.8rem' }}>{c.ticketNumber}</td>
                      <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{c.category?.replace(/_/g, ' ')}</td>
                      <td><span className={`badge badge-${c.status}`} style={{ textTransform: 'capitalize' }}>{c.status?.replace(/_/g, ' ')}</span></td>
                      <td><span className={`badge badge-${c.priority === 'critical' ? 'danger' : c.priority === 'high' ? 'warning' : 'info'}`} style={{ textTransform: 'capitalize' }}>{c.priority}</span></td>
                      <td style={{ fontSize: '.8rem' }}>{fmtDate(c.createdAt)}</td>
                      <td>
                        <Link to={`/complaints/${c._id}`} className="btn btn-secondary btn-sm">
                          <ExternalLink size={12} /> View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showPayModal && (
        <PaymentModal
          customer={customer}
          onClose={() => setShowPayModal(false)}
          onSuccess={() => { setShowPayModal(false); loadData(); }}
        />
      )}

      {showEditModal && (
        <EditCustomerModal
          customer={customer}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updated) => { setCustomer(updated); setShowEditModal(false); }}
        />
      )}
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{label}</span>
    <span style={{ fontSize: '.85rem', fontWeight: 500, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit' }}>{value || '—'}</span>
  </div>
);
